/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type {
  RoomConfig,
  SnapRecentSearchEntry,
  SnapRecentSearchesStore,
} from '../types';
import { TripType } from '../types';

export interface SnapSearchHistoryRoute {
  origin: string;
  destination: string;
  originStationName: string;
  destinationStationName: string;
  isAAGoRoute: false;
}

export interface SnapSearchHistoryPassengers {
  adult: number;
  child: number;
  infant: number;
  youngAdult: number;
}

export interface SnapSearchHistoryEntry {
  route: SnapSearchHistoryRoute;
  date: {
    depart: string;
    return?: string;
  };
  passengers: SnapSearchHistoryPassengers[];
  tripType: 'O' | 'R';
  createdAt: string;
  promoCode: string;
}

const aggregatePassengers = (rooms: RoomConfig[] = []) =>
  rooms.reduce(
    (totals, room) => ({
      adult: totals.adult + (room.adults ?? 0),
      child: totals.child + (room.children?.length ?? 0),
      infant: totals.infant + (room.infants ?? 0),
      youngAdult: totals.youngAdult,
    }),
    { adult: 0, child: 0, infant: 0, youngAdult: 0 },
  );

export const buildSnapSearchHistoryEntry = (
  entry?: SnapRecentSearchEntry | null,
): SnapSearchHistoryEntry | null => {
  const origin = entry?.from?.StationCode?.trim();
  const destination = entry?.to?.StationCode?.trim();
  const depart = entry?.departDate?.trim();

  if (!entry || !origin || !destination || !depart) {
    return null;
  }

  const returnDate = entry.returnDate?.trim();

  return {
    route: {
      origin,
      destination,
      originStationName: entry.from.CityName || entry.from.StationName || '',
      destinationStationName: entry.to.CityName || entry.to.StationName || '',
      isAAGoRoute: false,
    },
    date: {
      depart,
      ...(returnDate ? { return: returnDate } : {}),
    },
    passengers: [aggregatePassengers(entry.rooms)],
    tripType: entry.tripType === TripType.ONE_WAY ? 'O' : 'R',
    createdAt: new Date(entry.timestamp).toISOString(),
    promoCode: '',
  };
};

/**
 * Flattens the snap recent-searches persist cache into the list shape the
 * native bridge expects (`NativeModules.ReactToNative.sendSearchHistory`),
 * matching flights' `flattenRecentSearchesForHistory`: send the whole stored
 * list on every search, not just the one just submitted.
 */
export const flattenSnapRecentSearchesForHistory = (
  store?: SnapRecentSearchesStore,
): SnapSearchHistoryEntry[] => {
  if (!store?.recentSearches?.length) {
    return [];
  }

  return store.recentSearches.flatMap((entry) => {
    const historyEntry = buildSnapSearchHistoryEntry(entry);
    return historyEntry ? [historyEntry] : [];
  });
};
